/**
 * The password-reset link is `/reset-password/?token=<JWT>` — a single-use,
 * 30-minute, bearer-equivalent secret sitting in the URL. Analytics (PostHog) and
 * monitoring (Sentry) boot at module load and capture the initial page URL, which
 * would leak the token to third-party stores before any React effect can scrub it.
 *
 * So we strip it from the address bar SYNCHRONOUSLY at startup — before
 * initAnalytics()/initMonitoring() run — and stash it in memory for the
 * /reset-password page to consume. `stripTokenParam` is also wired into the
 * analytics/monitoring property sanitizers as defense-in-depth.
 */

let stashedResetToken: string | null = null;

/** Remove a `token` query param from a full URL or a `path?query` string. */
export function stripTokenParam(url: string): string {
  if (!url || !url.includes('token=')) return url;
  try {
    const u = new URL(url);
    u.searchParams.delete('token');
    return u.toString();
  } catch {
    // Relative form (pathname + search): strip token, then tidy separators.
    const cleaned = url.replace(/([?&])token=[^&#]*/gi, '$1');
    return cleaned.replace(/[?&]+(#|$)/g, '$1').replace(/([?&])&+/g, '$1');
  }
}

/**
 * Call once at startup, BEFORE analytics/monitoring init. On the reset page, pull
 * the token out of the URL into memory and scrub the address bar so the boot
 * pageview / session replay never sees it. No-op everywhere else.
 */
export function captureAndScrubResetToken(): void {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname.replace(/\/+$/, '');
  if (path !== '/reset-password') return;
  const token = new URLSearchParams(window.location.search).get('token');
  if (!token) return;
  stashedResetToken = token;
  window.history.replaceState({}, '', '/reset-password');
}

/** The reset token captured at startup, if any. */
export function takeResetToken(): string | null {
  return stashedResetToken;
}
